/**
 * Recargar el saldo de Smarty Drop — el mismo modal en la Billetera y en la venta.
 *
 * ── POR QUÉ ES UN COMPONENTE ──────────────────────────────────────────────────────────────────
 * Vivía dentro de la página Billetera, y «Recargar saldo» desde una venta navegaba hasta allá. El
 * founder (20-sep-2026): «la opción de recargar saldo en una nueva venta me lleva a la página de
 * recarga, sin salir modal». Ahora es un modal que se abre donde se está, con la anatomía de Stripe
 * Checkout (`ModalDePago`): a la izquierda cuánto y qué compra —los montos como fichas, el «Amount
 * to buy» de OpenAI—; a la derecha el medio, la cuenta de Smarty y el comprobante.
 *
 * ⭐ LOS MONTOS SUGERIDOS YA SABEN QUÉ COMPRAN: «lo que falta» primero, y múltiplos del ticket
 *    promedio REAL del revendedor («S/ 110 · ≈ 5 pedidos»). Sin ticket no hay múltiplos: inventarlos
 *    sería una cifra con aire de dato.
 *
 * 🚨 LA CAPTURA ES OBLIGATORIA. El código de operación es un número que cualquiera puede escribir;
 *    la captura es lo único que muestra monto, fecha y destinatario juntos. Una recarga aprobada
 *    sin comprobante cuesta plata que nunca entró.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAdminToastOptional } from '@/contexts/AdminToastContext';
import { PaymentProofField } from '@/components/operations/PaymentProofField';
import { cuentaParaMetodo, metodosDisponibles } from '@/components/dropshipping/ADondeDepositar';
import { uploadPaymentProof } from '@/lib/operations/ordersApi';
import { declararPagoDrop, type MetodoDrop, type SaldoDrop } from '@/lib/dropshipping/datosDeDrop';
import {
  ModalDePago, TituloDelPago, FichasDeMetodo, DestinoDelPago, CamposDelComprobante, BotonDePagar, Legal, MontosSugeridos, PasoListo, Aviso,
  ICONO, CURRENCY, type LineaDelResumen,
} from './ModalDePago';

/** Los montos que se ofrecen, con lo que compran. Pura, para poder probarla. */
export function sugerenciasDeRecarga(saldo: SaldoDrop | null | undefined, sugerido?: number): Array<{ valor: number; para: string }> {
  const out: Array<{ valor: number; para: string }> = [];
  const falta = sugerido && sugerido > 0
    ? sugerido
    : Math.max(0, Number(saldo?.falta_total ?? 0) - Number(saldo?.saldo ?? 0));
  if (falta > 0) out.push({ valor: Math.ceil(falta), para: 'lo que falta para destrabar' });
  const ticket = Number(saldo?.ticket_promedio ?? 0);
  if (ticket > 0) {
    for (const n of [5, 10]) {
      const v = Math.ceil(ticket * n);
      if (!out.some((o) => o.valor === v)) out.push({ valor: v, para: `≈ ${n} pedidos` });
    }
  }
  return out;
}

const MARCA: Record<string, { texto: string; color: string; titulo: string }> = {
  yape: { texto: 'yape', color: '#742284', titulo: 'Yape' },
  plin: { texto: 'plin', color: '#00b3a4', titulo: 'Plin' },
  transfer: { texto: 'BCO', color: '#1a1f36', titulo: 'Transferencia' },
  cash: { texto: 'S/', color: '#1a1f36', titulo: 'Efectivo' },
};

export function RecargarSaldoModal({ open, onClose, tenantId, saldo, sugerido, para, onRecargado }: {
  open: boolean;
  onClose: () => void;
  tenantId: string;
  saldo: SaldoDrop | null | undefined;
  /** Cuánto conviene recargar (p. ej. lo que le falta para esta venta). Se ofrece primero y ya escrito. */
  sugerido?: number;
  /** Para qué es (las líneas de la venta desde la que se abre): se ven en el resumen, como en Pagar. */
  para?: LineaDelResumen[];
  onRecargado?: () => void;
}) {
  /*
   * 🪤 EL TOAST ES OPCIONAL: este modal también se abre desde la billetera del ENCABEZADO, que vive
   *    fuera del proveedor de toasts (arriba del Frame). Sin proveedor, los avisos van al log y el
   *    modal igual muestra su estado final; tumbar el panel por un aviso sería lo contrario de avisar.
   */
  const toastReal = useAdminToastOptional();
  const toast = toastReal ?? { success: (t: string, d?: string) => console.info(t, d ?? ''), error: (t: string, d?: string) => console.warn(t, d ?? ''), info: (t: string, d?: string) => console.info(t, d ?? '') };
  const queryClient = useQueryClient();
  const cuentas = useMemo(() => saldo?.cuentas ?? [], [saldo]);
  const opciones = useMemo(() => metodosDisponibles(cuentas), [cuentas]);
  const sugerencias = useMemo(() => sugerenciasDeRecarga(saldo, sugerido), [saldo, sugerido]);
  const disponible = Number(saldo?.saldo ?? 0);

  const [metodo, setMetodo] = useState<MetodoDrop>('yape');
  const [monto, setMonto] = useState('');
  const [referencia, setReferencia] = useState('');
  const [captura, setCaptura] = useState<File | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [listo, setListo] = useState(false);

  // Cada apertura arranca limpia: el método que Smarty acepta primero, y el monto sugerido ya escrito.
  useEffect(() => {
    if (!open) return;
    setMetodo((opciones[0]?.value ?? 'yape') as MetodoDrop);
    setMonto(sugerido && sugerido > 0 ? String(Math.ceil(sugerido)) : String(sugerencias[0]?.valor ?? ''));
    setReferencia('');
    setCaptura(null);
    setListo(false);
  }, [open, opciones, sugerido, sugerencias]);

  const declarar = useCallback(async () => {
    const importe = Number(monto);
    if (!Number.isFinite(importe) || importe <= 0) { toast.error('Pon el monto que depositaste'); return; }
    if (!referencia.trim()) { toast.error('Pon el código de operación'); return; }
    if (!captura) { toast.error('Adjunta la captura del pago: es lo que permite verificar el depósito'); return; }
    setEnviando(true);
    try {
      // La captura se sube ANTES de declarar: si se declarara primero y la subida fallara, quedaría una recarga sin nada que mirar.
      const comprobanteUrl = await uploadPaymentProof(tenantId, 'recarga', captura);
      await declararPagoDrop({ tenantId, orderId: null, metodo, monto: importe, referencia, comprobanteUrl });
      setListo(true);
      onRecargado?.();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'No se pudo registrar la recarga');
    } finally {
      setEnviando(false);
    }
  }, [monto, referencia, captura, tenantId, metodo, queryClient, toast, onRecargado]);

  const importe = Number(monto) || 0;
  const cuenta = cuentaParaMetodo(cuentas, metodo);
  const marca = MARCA[metodo] ?? MARCA.transfer;

  return (
    <ModalDePago
      open={open}
      onClose={onClose}
      titulo="Recargar saldo"
      comercio="Smarty Drop"
      inicial="SD"
      colorDelLogo="#0e8a5f"
      pagarA="Recargar tu saldo"
      monto={importe}
      items={para}
      izquierdaExtra={sugerencias.length > 0 ? <MontosSugeridos opciones={sugerencias} valor={importe} onElegir={(v) => setMonto(String(v))} /> : undefined}
      sumas={[
        ...(para && para.length ? [{ etiqueta: 'Pedido a pagar con esto', valor: CURRENCY.format(para.reduce((a, b) => a + b.importe, 0)) }] : []),
        { etiqueta: 'Saldo actual', valor: CURRENCY.format(disponible) },
        { etiqueta: 'Comisión', valor: `${CURRENCY.format(0)} · recargar es gratis` },
        { etiqueta: 'Saldo después', valor: CURRENCY.format(disponible + importe), total: true },
      ]}
    >
      {listo ? (
        <PasoListo titulo="Recarga enviada" texto="Cuando se verifique el depósito, el saldo aparece. Te avisamos." onVolver={onClose} />
      ) : (
        <>
          <TituloDelPago>Recargar saldo</TituloDelPago>
          {opciones.length > 0 ? (
            <FichasDeMetodo
              valor={metodo}
              onCambiar={(v) => setMetodo(v as MetodoDrop)}
              fichas={opciones.map((o) => ({
                value: o.value as MetodoDrop,
                label: o.value === 'transfer' ? 'Transferencia' : o.label,
                detalle: 'a Smarty',
                icono: ICONO[o.value === 'transfer' ? 'transfer' : o.value === 'plin' ? 'plin' : 'yape'],
              }))}
            />
          ) : <Aviso>Todavía no hay una cuenta de Smarty cargada para recibir recargas.</Aviso>}
          {cuenta?.number ? (
            <DestinoDelPago marca={marca.texto} color={metodo === 'yape' || metodo === 'plin' ? marca.color : '#0e8a5f'} titulo={`${marca.titulo} a Smarty${cuenta.bank && metodo === 'transfer' ? ` · ${cuenta.bank}` : ''}`} numero={cuenta.number} titular={[cuenta.cci ? `CCI ${cuenta.cci}` : null, cuenta.holder].filter(Boolean).join(' · ')} />
          ) : null}
          <CamposDelComprobante monto={monto} onMonto={setMonto} referencia={referencia} onReferencia={setReferencia} />
          <PaymentProofField file={captura} onChange={setCaptura} obligatorio compacto />
          <BotonDePagar onClick={() => void declarar()} loading={enviando}>Recargar {CURRENCY.format(importe)}</BotonDePagar>
          <Legal>La plata entra a Smarty, no al almacén. Se acredita cuando se verifica el depósito; con saldo, tus próximos pedidos se pagan al instante.</Legal>
        </>
      )}
    </ModalDePago>
  );
}
